/**
 * please think twice before adding a new app-level dependency,
 * it'll have effect to the pageload and parsing time of the app
 */
// htmx configuration and the stale-asset reload live in boot.js, which must load before this entry.
import {Button} from './modules/button.js';
import {Notification} from './modules/notification.js';
import {PageSpinner, Spinner} from './modules/spinner.js';
import {Tooltip} from './modules/tooltip.js';
import {Auth} from './modules/auth.js';
import {AnalyticsListener} from './modules/analyticsListener.js';
import {SmoothAnchor} from './modules/smoothAnchor.js';
import {initLayoutVariables} from './modules/layoutVariables.js';
import {lazyLoadCollapsibleTextComponents} from './modules/lazyLoadCollapsibleTextComponents.js';
import {deferToIdle} from './utils/deferToIdle.js';
import {t} from './localization/translate.js';

import './behaviors/cta.js';
import './behaviors/dropdown.js';
import './behaviors/formValidation.js';
import './behaviors/lazyLoader.js';
import './behaviors/offcanvas.js';
import './behaviors/footerNewsletterForm.js';

// The search modal binds its buttons and shortcut only where a page shows a way into it.
if (document.querySelector('.js-modalToggleSearchButton, .js-mainNavigationSearchbar')) {
    import('./behaviors/search/modalSearch.js').then(() => {});
}

// The countdown brings the customized-built-in polyfill along; an announcement bar may add one later.
if (document.querySelector('ixdf-countdown, [data-page-announcement]')) {
    import('./components/countdown.js').then(() => {});
}

if (document.querySelector('[data-template-download-form]')) {
    import('./behaviors/templateDownloadForm.js').then(() => {});
}

if (document.querySelector('[data-newsletter-subscribe-form]')) {
    import('./behaviors/newsletterSubscribeForm.js').then(() => {});
}

if (document.querySelector('.js-copyText')) {
    import('./behaviors/copyToClipboard.js').then(() => {});
}

if (document.querySelector('button[data-toggle], div[data-toggle]')) {
    import('./behaviors/toggleContent.js').then(() => {});
}

if (document.querySelector('.js-readMore')) {
    import('./modules/readMore.js').then(({ReadMore}) => ReadMore.init());
}

// Announcement Bars
if (document.querySelector('[data-page-announcement]')) {
    import('./behaviors/pageAnnouncementBar.js').then(() => {});
}

// RTE Content Animator is lazy-loaded only when RTE content exists on the page
if (document.querySelector('[data-rte-content], .rte-content, .rteContent')) {
    import('./behaviors/rteContentAnimator.js').then(() => {});
}

if (document.querySelector('toc-container')) {
    import('./components/tocContainer.js').then(() => {});
}

/** @type {import('../../node_modules/htmx.org/dist/htmx.esm.d.ts').htmx} htmx */
import htmx from 'htmx.org';
import './pages/__sharedDialogs/termsAcceptance.js';

// x-ui.local-time pushes this script on a full page load. onLoad also runs for every htmx swap, so it covers fragments
// (no layout for the push to reach) and raw <time is> markup. Both paths load one module URL, which runs once.
htmx.onLoad((root) => {
    if (root.querySelector?.('time[is="ixdf-local-time"]')) {
        import('./components/localTime.js').then(() => {});
    }
});
import {setupDialogSupport} from './modules/dialog.js';
import {registerEventLogger} from './modules/eventLogger.js';

/** Selector for elements that represent playable videos on a page. */
const VIDEO_ELEMENTS_SELECTOR =
    'video,iframe[data-src*=youtube],iframe[src*=youtube],iframe[src*=vimeo],iframe[data-src*=vimeo]';

/**
 * Display Alerts based on elements with data-alert attribute
 */
function displayAlerts() {
    [...document.querySelectorAll('[data-alert]')].forEach(
        /** @param {HTMLElement} element */
        (element) => {
            Notification[element.dataset.alertType](
                // `html: true` because FlashUiNotification already escaped anything that was plain text.
                {message: element.dataset.alertMessage, title: element.dataset.alertTitle, html: true},
                element.parentElement
            );
        }
    );
}

/**
 * Listen notification:show events to display Notifications
 * Good to:
 * - not import Notification in multiple js files
 * - use different Notifications on different contexts (e.g. on Nova)
 *
 * event.detail.container is optional: a CSS selector string, or an Element, to render into.
 * Defaults to event.target (the element that dispatched/triggered the event, e.g. an htmx
 * HX-Trigger response fires this on the element that issued the request).
 */
function listenNotifications() {
    document.addEventListener('notification:show', function(event) {
        let messageType = event.detail.type || 'error';
        if (!['success', 'info', 'warning', 'error'].includes(messageType)) {
            messageType = 'error';
        }

        let container = event.target;
        if (typeof event.detail.container === 'string') {
            const resolved = document.querySelector(event.detail.container);
            if (resolved) {
                container = resolved;
            } else {
                window.dispatchEvent(new CustomEvent('log:error', {
                    detail: new Error(`notification:show container selector matched nothing: "${event.detail.container}"`),
                }));
            }
        } else if (event.detail.container instanceof Element) {
            container = event.detail.container;
        }

        // Notification.error()
        Notification[messageType](
            event.detail.message || `undefined ${messageType} message}`,
            container
        );
    });
}

/**
 * Listen spinner:xxx events to display Spinner
 * Good to:
 * - not import Spinner in multiple js files
 * - use different Spinner on different contexts (e.g. on Nova)
 */
function listenSpinners() {
    document.addEventListener('spinner:start', function(event) {
        const message = event.detail.message || t('Loading…');
        const details = event.detail.details || '';

        event.target instanceof HTMLDocument
            ? PageSpinner.start(message, details)
            : Spinner.createAndStart(event.target, message, details);
    });

    document.addEventListener('spinner:stop', function(event) {
        event.target instanceof HTMLDocument
            ? PageSpinner.stop()
            : new Spinner(event.target).stop();
    });
}

/**
 * Preloads a source code for video component only if there are any videos on the page.
 * The initialization of a player instance for videos is up to {@link components/video}.
 */
function preloadVideoComponent() {
    const firstVideo = document.querySelector(VIDEO_ELEMENTS_SELECTOR);
    if (!firstVideo) return;

    const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
            import('./behaviors/video.js');
            observer.disconnect();
        }
    }, {
        rootMargin: '200px', // Start loading when video is near viewport
    });

    observer.observe(firstVideo);
}

/**
 * A stale-asset reload (modules/staleAssetRecovery.js) cancels in-flight Bugsnag requests, so the fresh page reports
 * it once the event logger listens. Deferred to idle so the async Bugsnag client can load.
 */
function reportStaleAssetRecovery() {
    // Written by modules/staleAssetRecovery.js; importing it here would split it into a chunk of its own.
    const PENDING_REPORT_KEY = 'ixdf:preloadErrorPendingReport';

    deferToIdle(() => {
        try {
            const failedAsset = sessionStorage.getItem(PENDING_REPORT_KEY);
            if (failedAsset) {
                sessionStorage.removeItem(PENDING_REPORT_KEY);
                window.dispatchEvent(new CustomEvent('log:info', {
                    detail: new Error(`Recovered from stale asset via reload: ${failedAsset}`),
                }));
            }
        } catch {
            // sessionStorage unavailable: nothing was stored to report.
        }
    });
}

/**
 * Init application
 */
function runApp() {
    // === Critical path (must run immediately) ===
    registerEventLogger();
    reportStaleAssetRecovery();
    setupDialogSupport();

    if (Auth.check()) {
        import('./behaviors/sessionVerifier.js');
    }

    displayAlerts();
    listenNotifications();
    listenSpinners();
    preloadVideoComponent();
    lazyLoadCollapsibleTextComponents();
    initLayoutVariables();

    // === Deferred (non-critical, run when idle) ===
    deferToIdle(() => {
        Button.init();
        Tooltip.applyTo(document.body);
        new AnalyticsListener().registerListeners();
        new SmoothAnchor();
    });
}

// init page when the document is ready
document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', runApp) : runApp();

// @ts-ignore
if (import.meta.env.VITE_APP_ENV === 'production') {
    // eslint-disable-next-line no-console
    console.info(
        '------------------------------------------------------------------------\n\nWant to help us write even better code?\n\nThen come work with us: https://ixdf.org/about/careers\n\n------------------------------------------------------------------------'
    );
}
